import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, TextField, Typography } from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { useField, useFormikContext } from 'formik';
import { showToast } from '@klpkonsern/ui';

import { formatDate, parseSafeDate } from '@klp/utils/dateTimeFormatter';
import { Block } from '@klp/design';
import type { FormField } from '../interfaces';
import type { ProjectBudgetInfo } from '../services/rtkApi';
import {
    useGetProsjektByggListQuery,
    useLazyGetProjectBudgetDtosQuery,
    useLazyGetProjectBudgetInfoQuery,
} from '../services/rtkApi';
import {
    findCamundaValidation,
    getLocalizedLabel,
    handleBuildingOptionByValue,
} from '../utils';
import { FormikNumericFormat } from './FormikNumericFormat';
import classNames from 'classnames';
import { getFormFieldStyles } from '../formFieldStyles';

export const BudsjettinfoSelector = ({
    field,
    fields,
    isReadOnly,
    isRequired,
    isHidden,
}: {
    field: FormField;
    fields: Array<FormField>;
    isReadOnly: boolean;
    isRequired: boolean;
    isHidden?: boolean;
}) => {
    const { t } = useTranslation();
    const classes = getFormFieldStyles();
    const [formikField, meta] = useField<string | undefined>(field.id);
    const { setFieldValue, values } = useFormikContext();
    const [budgetnumberValue, setBudgetNumberValue] = useState(
        formikField.value,
    );
    const [fetchedFirmaId, setFetchedFirmaId] = useState('');
    const [
        getProjectBudgets,
        { data: projectBudgetDtos, isLoading, isFetching },
    ] = useLazyGetProjectBudgetDtosQuery();
    const [getProjectBudgetInfo, { data: projectBudgetInfo }] =
        useLazyGetProjectBudgetInfoQuery();
    const { data: byggData } = useGetProsjektByggListQuery();

    const aarsbudsjettId = 'aarsbudsjett';
    const [aarsbudsjettFormikField, aarsbudsjettFormikMeta] =
        useField(aarsbudsjettId);
    const aarsbudsjettField = fields?.find(
        (f) => f.id === aarsbudsjettFormikField?.name,
    );
    const aarsbudsjettRequired = findCamundaValidation(
        'required',
        aarsbudsjettField?.validation,
    );
    const aarsbudsjettReadOnly = findCamundaValidation(
        'readonly',
        aarsbudsjettField?.validation,
    );

    useEffect(() => {
        if (isReadOnly || !values?.['eiendomsnavn']) return;

        const bygg = handleBuildingOptionByValue(
            values['eiendomsnavn'],
            byggData,
        );

        if (
            bygg?.firmaId &&
            bygg?.firmaId !== '' &&
            bygg?.firmaId !== fetchedFirmaId
        ) {
            const fromDate = new Date(new Date().getFullYear(), 0, 1);
            getProjectBudgets({
                firmaId: bygg.firmaId,
                fromDate: fromDate.toISOString().split('T')[0],
            });
            setFetchedFirmaId(bygg.firmaId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values, byggData]);

    useEffect(() => {
        if (budgetnumberValue === undefined || budgetnumberValue === '') return;

        const projectBudgets = projectBudgetDtos?.map(
            (budget) => budget.budsjettnummer,
        );
        if (
            (isReadOnly || projectBudgets?.includes(budgetnumberValue)) &&
            budgetnumberValue !== projectBudgetInfo?.budsjettnummer
        ) {
            getProjectBudgetInfo({ budsjettnummer: budgetnumberValue })
                .unwrap()
                .catch((e) => {
                    if (
                        e?.data?.includes(
                            'Could not find any budgets for budget number',
                        )
                    ) {
                        return;
                    }
                    showToast({
                        body: t('general.error.message'),
                        type: 'error',
                    });
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [budgetnumberValue, projectBudgetDtos]);

    useEffect(() => {
        if (!isReadOnly && projectBudgetInfo?.budsjettnummer) {
            setFieldValue(
                aarsbudsjettId,
                projectBudgetInfo.budgetExMva.toString(),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectBudgetInfo]);

    const handleChangeValue = (value: string | undefined) => {
        setBudgetNumberValue(value);
        setFieldValue(field.id, value || '');
    };

    const initialValue = budgetnumberValue
        ? projectBudgetDtos?.find(
              (budget) => budget.budsjettnummer === budgetnumberValue,
          ) || ({ budsjettnummer: budgetnumberValue } as ProjectBudgetInfo)
        : null;
    return (
        <>
            <Autocomplete
                id="budsjettnummer"
                selectOnFocus={true}
                clearOnBlur={true}
                handleHomeEndKeys={true}
                disabled={isReadOnly}
                options={projectBudgetDtos || []}
                getOptionSelected={(option, value) =>
                    option.budsjettnummer === value?.budsjettnummer
                }
                getOptionLabel={(option) =>
                    option.beskrivelse
                        ? `${option.budsjettnummer} - ${option.beskrivelse}`
                        : option.budsjettnummer
                }
                value={initialValue}
                onChange={(event, value) => {
                    handleChangeValue(value?.budsjettnummer);
                }}
                loading={isLoading || isFetching}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        {...formikField}
                        fullWidth={true}
                        multiline={true}
                        className={classNames({
                            [classes.editableField]: !isReadOnly,
                            [classes.readOnlyField]: isReadOnly,
                        })}
                        required={isRequired}
                        variant="outlined"
                        disabled={isReadOnly}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {isLoading || isFetching ? (
                                        <CircularProgress color="inherit" />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                        label={getLocalizedLabel(
                            `project.formFields.${field?.id}`,
                            field?.label,
                            field?.id,
                        )}
                        helperText={meta.touched ? meta.error : ''}
                        error={meta.touched && Boolean(meta.error)}
                    />
                )}
            />
            {projectBudgetInfo &&
            budgetnumberValue &&
            budgetnumberValue === projectBudgetInfo.budsjettnummer ? (
                <Block paddingTop="tiny">
                    <Alert severity="info">
                        <Typography style={{ fontWeight: 'bold' }}>
                            {t('prosjekt.budsjettnummer.title', {
                                number: projectBudgetInfo.budsjettnummer,
                            })}
                        </Typography>
                        <Typography variant="body1">
                            {t('prosjekt.budsjettnummer.period', {
                                from: formatDate(
                                    parseSafeDate(projectBudgetInfo.from),
                                ),
                                to: formatDate(
                                    parseSafeDate(projectBudgetInfo.to),
                                ),
                            })}
                        </Typography>
                        <Typography variant="body1">
                            {t('prosjekt.budsjettnummer.budget', {
                                exMva: projectBudgetInfo.budgetExMva.toLocaleString(
                                    'nb-NO',
                                ),
                                mva: projectBudgetInfo.mva.toLocaleString(
                                    'nb-NO',
                                ),
                                total: projectBudgetInfo.budgetTotal.toLocaleString(
                                    'nb-NO',
                                ),
                            })}
                        </Typography>
                    </Alert>
                </Block>
            ) : (
                <Block paddingTop="compact">
                    <FormikNumericFormat
                        thousandSeparator={' '}
                        decimalSeparator=","
                        variant="outlined"
                        className={classNames({
                            [classes.editableField]: !isReadOnly,
                            [classes.readOnlyField]: isReadOnly,
                        })}
                        id={aarsbudsjettField?.id}
                        isRequired={!isHidden && aarsbudsjettRequired}
                        isReadOnly={aarsbudsjettReadOnly}
                        label={getLocalizedLabel(
                            `project.formFields.aarsbudsjett`,
                            aarsbudsjettField?.label,
                            aarsbudsjettField?.id,
                        )}
                        defaultValue={
                            aarsbudsjettField?.value ||
                            aarsbudsjettField?.defaultValue
                        }
                        helperText={
                            aarsbudsjettFormikMeta.touched
                                ? aarsbudsjettFormikMeta.error
                                : ''
                        }
                        error={
                            aarsbudsjettFormikMeta.touched &&
                            Boolean(aarsbudsjettFormikMeta.error)
                        }
                    />
                </Block>
            )}
        </>
    );
};
